import React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ChatBubble from "./ChatBubble";
import Logo from "./Logo";
import WizardLiveView from "./WizardLiveView";
import {
  Scales,
  UserCircle,
  SignOut,
  PlusCircle,
  ShieldCheck,
  BookOpen,
} from "@phosphor-icons/react";

const NAV_ITEMS = [
  { to: "/dashboard", label: "Einstufung", icon: Scales, available: true },
];

export default function UserLayout({ children, title, subtitle, actions }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Iter 20.08 — Demo-Modus: DemoToolPage rendert bereits DemoLayout als
  // Chrome. UserLayout darf sich in diesem Fall nicht mit Sidebar/TopBar
  // überlagern, sondern reicht nur den Content durch. Dedupliziert wird
  // über den URL-Präfix `/demo/`, damit wir keinen Prop-Weg durch die
  // 1600-Zeilen-EinstufungToolPage schleusen müssen.
  if (location.pathname.startsWith("/demo/")) {
    return <div data-testid="user-layout-demo-passthrough">{children}</div>;
  }

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // "Neue Einstufung" — if the tool is already mounted, ask it (via window
  // event) to optionally confirm before discarding existing inputs. If the
  // user is elsewhere (e.g. /profil), navigating to /dashboard mounts a
  // fresh tool instance with empty state — no event needed.
  const handleNewEinstufung = () => {
    if (location.pathname === "/dashboard") {
      window.dispatchEvent(new CustomEvent("et:new-einstufung"));
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen bg-ink-50 flex" data-testid="user-layout">
      {/* SIDEBAR */}
      <aside className="hidden lg:flex flex-col w-72 shrink-0 bg-white border-r border-ink-200 sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-ink-200">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="block text-left transition-crisp hover:opacity-80"
            data-testid="user-sidebar-logo"
            title="Zur Einstufung"
          >
            <Logo size="md" />
          </button>
        </div>

        <div className="px-3 pt-4 pb-2">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-warm-500 px-1 mb-2">
            Hauptmenü
          </div>
          <nav className="flex flex-col gap-1" data-testid="user-sidebar-nav">
            {NAV_ITEMS.map(({ to, label, icon: Icon, available }) => {
              if (!available) {
                return (
                  <div
                    key={to}
                    data-testid={`user-nav-${to.split("/").pop()}-disabled`}
                    className="group flex items-center justify-between gap-3 px-3 py-2 rounded-sm text-sm font-medium text-warm-400 cursor-not-allowed select-none"
                  >
                    <span className="flex items-center gap-3">
                      <Icon size={16} className="text-warm-300" />
                      {label}
                    </span>
                    <span className="text-[9px] uppercase tracking-widest text-warm-400 border border-warm-200 rounded-sm px-1.5 py-0.5">
                      bald
                    </span>
                  </div>
                );
              }
              return (
                <NavLink
                  key={to}
                  to={to}
                  end
                  data-testid={`user-nav-${to.split("/").pop()}`}
                  className={({ isActive }) =>
                    [
                      "group flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-crisp",
                      isActive
                        ? "bg-ink-900 text-white"
                        : "text-warm-700 hover:bg-ink-100 hover:text-ink-900",
                    ].join(" ")
                  }
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        size={16}
                        weight={isActive ? "bold" : "regular"}
                        className={isActive ? "text-brand-400" : "text-warm-500 group-hover:text-ink-900"}
                      />
                      {label}
                    </>
                  )}
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Live-Mitschrift — fills the middle space (progressive disclosure;
            only shown once the user has entered at least one value). */}
        <div className="flex-1 min-h-0 border-t border-ink-200 mt-3">
          <WizardLiveView />
        </div>

        <div className="border-t border-ink-200 p-3 space-y-2">
          <div className="px-1">
            <div className="text-[9px] uppercase tracking-widest font-semibold text-warm-500 mb-0.5">
              Angemeldet als
            </div>
            <div className="font-medium text-ink-900 text-[13px] truncate leading-tight">
              {user?.name}
            </div>
            <div className="text-[11px] text-warm-600 truncate leading-tight">{user?.email}</div>
          </div>
          {/* Iter 19.63.11 — Kompakte Sticky-Toolbar:
              • „Neue Einstufung" bleibt full-width Primary (wichtigste Aktion)
              • Admin / Profil / Logout → Icon-Buttons mit Tooltip in einer Zeile */}
          <button
            onClick={handleNewEinstufung}
            data-testid="user-sidebar-new-einstufung"
            className="w-full inline-flex items-center justify-center gap-2 rounded-sm bg-brand-400 px-3 py-1.5 text-xs font-semibold text-white transition-crisp hover:bg-brand-500 shadow-[0_1px_0_rgba(0,0,0,0.04)]"
          >
            <PlusCircle size={13} weight="bold" />
            Neue Einstufung
          </button>
          <div className="flex items-center gap-1.5">
            {((user?.role || "user") === "admin" || (user?.role || "user") === "superadmin") && (
              <button
                onClick={() => navigate("/admin/users")}
                data-testid="user-sidebar-admin"
                className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-ink-900 bg-ink-900 px-2 py-1.5 text-[11px] font-semibold text-white transition-crisp hover:bg-ink-800"
                title="Zur Administration wechseln"
                aria-label="Zur Administration wechseln"
              >
                <ShieldCheck size={13} weight="bold" />
                <span className="hidden xl:inline">Admin</span>
              </button>
            )}
            <button
              onClick={() => navigate("/profil")}
              data-testid="user-sidebar-profile"
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-[11px] font-medium text-ink-700 transition-crisp hover:bg-ink-100"
              title="Benutzerprofil öffnen"
              aria-label="Benutzerprofil öffnen"
            >
              <UserCircle size={13} weight="bold" />
              <span className="hidden xl:inline">Profil</span>
            </button>
            {/* Iter 19.63.108 — Hilfe-Button öffnet die Doku-Site in neuem
                Tab (User-Wunsch). Als `<a>` statt `<button>` für nativen
                Mittelklick-Support, Strg-Klick, „Link kopieren" etc. */}
            <a
              href="/hilfe"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="user-sidebar-help"
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm border border-ink-300 bg-white px-2 py-1.5 text-[11px] font-medium text-ink-700 transition-crisp hover:bg-ink-100"
              title="Dokumentation öffnen (neuer Tab)"
              aria-label="Dokumentation öffnen"
            >
              <BookOpen size={13} weight="bold" />
              <span className="hidden xl:inline">Hilfe</span>
            </a>
            <button
              onClick={handleLogout}
              data-testid="user-sidebar-logout"
              className="flex-1 inline-flex items-center justify-center gap-1 rounded-sm bg-ink-900 px-2 py-1.5 text-[11px] font-medium text-white transition-crisp hover:bg-ink-800"
              title="Abmelden"
              aria-label="Abmelden"
            >
              <SignOut size={13} weight="bold" />
              <span className="hidden xl:inline">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* MAIN */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden border-b border-ink-200 bg-white px-6 h-16 flex items-center justify-between sticky top-0 z-30">
          <button
            type="button"
            onClick={() => navigate("/dashboard")}
            className="inline-flex transition-crisp hover:opacity-80"
            data-testid="user-mobile-logo"
            title="Zur Einstufung"
          >
            <Logo size="sm" />
          </button>
          <button
            onClick={handleLogout}
            data-testid="user-mobile-logout"
            className="inline-flex items-center gap-1.5 rounded-sm border border-ink-300 px-3 py-1.5 text-xs font-medium text-ink-700"
          >
            <SignOut size={13} weight="bold" />
            Abmelden
          </button>
        </div>

        {/* Mobile nav strip — same actions as the sidebar footer */}
        <div className="lg:hidden border-b border-ink-200 bg-white px-4 py-2 flex gap-1 overflow-x-auto sticky top-16 z-20">
          {NAV_ITEMS.filter((n) => n.available).map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              end
              className={({ isActive }) =>
                [
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap transition-crisp",
                  isActive ? "bg-ink-900 text-white" : "text-warm-600 hover:bg-ink-100",
                ].join(" ")
              }
            >
              <Icon size={14} weight="bold" />
              {label}
            </NavLink>
          ))}
          <button
            onClick={handleNewEinstufung}
            data-testid="user-mobile-new-einstufung"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap bg-brand-400 text-white hover:bg-brand-500 transition-crisp"
          >
            <PlusCircle size={14} weight="bold" />
            Neue Einstufung
          </button>
          {((user?.role || "user") === "admin" || (user?.role || "user") === "superadmin") && (
            <button
              onClick={() => navigate("/admin/users")}
              data-testid="user-mobile-admin"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-semibold whitespace-nowrap bg-ink-900 text-white hover:bg-ink-800 transition-crisp"
            >
              <ShieldCheck size={14} weight="bold" />
              Administration
            </button>
          )}
          <button
            onClick={() => navigate("/profil")}
            data-testid="user-mobile-profile"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap text-warm-600 hover:bg-ink-100 transition-crisp"
          >
            <UserCircle size={14} weight="bold" />
            Benutzerprofil
          </button>
          {/* Iter 19.63.108 — Hilfe-Link Mobile. Öffnet Doku in neuem Tab. */}
          <a
            href="/hilfe"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="user-mobile-help"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-xs font-medium whitespace-nowrap text-warm-600 hover:bg-ink-100 transition-crisp"
          >
            <BookOpen size={14} weight="bold" />
            Hilfe
          </a>
        </div>

        {/* Page header — Iter 19.68.9: konditional, sodass Pages ohne
            `title` (z.B. EinstufungToolPage seit User-Wunsch „weniger
            visuelle Schwere am oberen Rand") den Header-Block komplett
            weglassen können. Andere Pages bleiben unverändert. */}
        {(title || actions) && (
          <header className="border-b border-ink-200 bg-white">
            <div className="px-6 lg:px-10 py-8 flex flex-wrap items-end justify-between gap-4">
              <div>
                {title && (
                  <h1
                    className="font-cabinet text-3xl sm:text-4xl tracking-tight text-ink-900 font-medium leading-none"
                    data-testid="user-page-title"
                  >
                    {title}
                  </h1>
                )}
                {subtitle && (
                  <p className="mt-3 max-w-2xl text-sm text-warm-700">{subtitle}</p>
                )}
              </div>
              {actions && <div className="flex items-center gap-2">{actions}</div>}
            </div>
          </header>
        )}

        {/* Main content */}
        <main className="flex-1 px-6 lg:px-10 py-8">{children}</main>
      </div>
      {/* Floating ET-Assistant — visible on every authenticated user page */}
      <ChatBubble />
    </div>
  );
}
